import React, { useContext, useEffect, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Text, mergeStyles } from '@bingads-webui-react/fabric-control';
import { Field, Radio, RadioGroup } from '@fluentui/react-components';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { areCriterionTargetsSet } from '../utils';

export const TARGET_OPTIONS = Object.freeze({
  BID_ONLY: 'BidOnly',
  TARGET_AND_BID: 'TargetAndBid',
});

export const TARGET_OPTIONS_TO_TEXT = Object.freeze({
  [TARGET_OPTIONS.BID_ONLY]: _TL_('Bid only'),
  [TARGET_OPTIONS.TARGET_AND_BID]: _TL_('Target and bid'),
});

export const TARGET_OPTIONS_TO_DESCRIPTION = Object.freeze({
  [TARGET_OPTIONS.BID_ONLY]: _TL_('Make bid adjustments for selected audiences without limiting ad reach.'),
  [TARGET_OPTIONS.TARGET_AND_BID]: _TL_('Limit the reach of your ads to the selected audiences only, with the option to adjust bids for your selections.'),
});

export const TargetItems = [
  {
    id: TARGET_OPTIONS.BID_ONLY,
    text: TARGET_OPTIONS_TO_TEXT[TARGET_OPTIONS.BID_ONLY],
    description: TARGET_OPTIONS_TO_DESCRIPTION[TARGET_OPTIONS.BID_ONLY],
  },
  {
    id: TARGET_OPTIONS.TARGET_AND_BID,
    text: TARGET_OPTIONS_TO_TEXT[TARGET_OPTIONS.TARGET_AND_BID],
    description: TARGET_OPTIONS_TO_DESCRIPTION[TARGET_OPTIONS.TARGET_AND_BID],
  },
];

export const TargetOrBidSelector = ({
  field, value, targets, negativeTargets, updateValidation, styles,
}) => {
  const i18n = useContext(I18nContext);

  const styleClass = mergeStyles(styles);

  const [isInitialized, setIsInitialized] = useState(false);

  const areTargetsLoaded = useMemo(
    () => targets || negativeTargets,
    [targets, negativeTargets]
  );

  const areTargetsSet = useMemo(
    () => areCriterionTargetsSet(targets, negativeTargets),
    [targets, negativeTargets]
  );

  useEffect(() => {
    if (!areTargetsLoaded) { return; }
    setIsInitialized(true);
  }, [areTargetsLoaded]);

  useEffect(() => {
    if (areTargetsLoaded && !areTargetsSet && !isInitialized) {
      updateValidation([], { [field]: true });
    }
  }, [areTargetsLoaded, areTargetsSet, isInitialized, field, updateValidation, value]);

  const onChange = useCallback((_, data) => {
    updateValidation([], { [field]: data.value === TARGET_OPTIONS.TARGET_AND_BID });
  }, [field, updateValidation]);

  return (
    <div className={styleClass}>
      { areTargetsSet &&
        <Field label={i18n.getString(_TL_('Targeting setting'))}>
          <RadioGroup value={value ? TARGET_OPTIONS.TARGET_AND_BID : TARGET_OPTIONS.BID_ONLY} onChange={onChange}>
            {TargetItems.map(option => (
              <>
                <Radio key={option.id} value={option.id} label={i18n.getString(option.text)} />
                <Text>{i18n.getString(option.description)}</Text>
              </>
          ))}
          </RadioGroup>
        </Field>
      }
    </div>
  );
};

TargetOrBidSelector.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  updateValidation: PropTypes.func.isRequired,
  targets: PropTypes.arrayOf(PropTypes.shape({})),
  negativeTargets: PropTypes.arrayOf(PropTypes.shape({})),
  styles: PropTypes.shape({}),
};

TargetOrBidSelector.defaultProps = {
  targets: null,
  negativeTargets: null,
  styles: {},
};

export const ValidatedTargetOrBidSelector = withCustomValidation(TargetOrBidSelector);
